import { NgModule } from '@angular/core';
import { CommonModule, KeyValuePipe } from '@angular/common';
import { AbstractControl, FormsModule, ReactiveFormsModule, ValidationErrors } from '@angular/forms';
import { MatButtonModule } from '@angular/material/button';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatIconModule } from '@angular/material/icon';
import { MatSlideToggleModule } from '@angular/material/slide-toggle';
import { MatTooltipModule } from '@angular/material/tooltip';
import { DragDropModule } from '@angular/cdk/drag-drop';

import { FormlyFieldConfig, FormlyModule } from '@ngx-formly/core';
import { NgSelectModule } from '@ng-select/ng-select';
import { FormlyMatToggleModule } from '@ngx-formly/material/toggle';
import { FormlyBootstrapModule } from '@ngx-formly/bootstrap';
import { FormlySelectModule } from '@ngx-formly/core/select';
import { FormlyMatFormFieldModule } from '@ngx-formly/material/form-field';

import { FormlyFieldSelectComponent } from './components/formly-field-select/formly-field-select.component';
import {
  FormlyFieldAsyncSelectComponent,
  FormlyFieldExpansionComponent,
  FormlyFieldInlineScrollableRadioComponent,
  FormlyFieldSelectionComponent,
  FormlySliderComponent,
  GenericSelectComponent,
} from './components';
import { NgxAngularQueryBuilderModule } from 'ngx-angular-query-builder';
import { FormlyFieldType, FormlyValidator, FormlyWrapper } from './model';
import { FormlyFieldAssetUploadComponent } from './components/asset-upload/asset-upload.component';
import { FormlyFieldButtonComponent } from './components/formly-field-button/formly-field-button.component';
import { FormControlsModule } from '@shared/components/form-controls/form-controls.module';
import { FormlyFieldRepeatSectionComponent } from './components/formly-field-repeat-section/formly-field-repeat-section.component';
import { FormlyWrapperInlineLabelComponent } from './wrappers/inline-label/inline-label.component';
import { FormlyWrapperLabelTooltipComponent } from './wrappers/label-tooltip/label-tooltip.component';
import { FormlyWrapperPanelComponent } from './wrappers/panel/panel.component';
import { NgxDaterangepickerMd } from 'ngx-daterangepicker-material';
import { FormlyFormComponent } from './components/formly-form/formly-form.component';
import { FormlyFieldAutoRepeatSectionComponent } from './components/formly-field-auto-repeat-section/formly-field-auto-repeat-section.component';
import { DragDropDirective, InputTrimDirective, NoScrollDirective } from '@core/directives';
import { SimplePreloaderComponent } from '@shared/preloader/simple-preloader/simple-preloader.component';
import { SafePipe } from '@corebase/layout';
import { FormlyFieldInputComponent } from './components/formly-field-input/formly-field-input.component';
import { FormlyFieldTextComponent } from './components/formly-field-text/formly-field-text.component';
import { applyModelChangeDebounce } from './extensions/default.extension';
import { DateRangeComponent } from './components/date-range/date-range.component';
import { FormlyColorPickerComponent } from './components/color-picker/color-picker.component';
import { ContentContainerComponent } from '@shared/preloader';
import { FormlyWrapperExpansionComponent } from './wrappers/expansion/expansion.component';
import { GenericButtonComponent } from '@shared/ui';
import { FormlyFieldDateComponent } from './components/date/date.component';
import { DatePickerComponent } from './components/date-picker/date-picker.component';
import { FormlySliderWrapperComponent } from './wrappers/slider-wrapper/slider-wrapper.component';

const FORMLY_CUSTOM_TEMPLATES = [
  {
    name: FormlyFieldType.StyledRadio,
    component: FormlyFieldInlineScrollableRadioComponent,
    wrappers: [FormlyWrapper.FormField],
  },
  {
    name: FormlyFieldType.DateRange,
    component: DateRangeComponent,
    wrappers: [FormlyWrapper.FormField],
  },
  {
    name: FormlyFieldType.DatePicker,
    component: DatePickerComponent,
    wrappers: [FormlyWrapper.FormField],
  },
  {
    name: FormlyFieldType.Select,
    component: FormlyFieldSelectComponent,
  },
  {
    name: FormlyFieldType.GenericSelect,
    component: GenericSelectComponent,
  },
  {
    name: FormlyFieldType.Input,
    component: FormlyFieldInputComponent,
    wrappers: [FormlyWrapper.FormField],
  },
  {
    name: FormlyFieldType.Slider,
    component: FormlySliderComponent,
    wrappers: [FormlyWrapper.Slider],
  },
  {
    name: FormlyFieldType.ColorPicker,
    component: FormlyColorPickerComponent,
    wrappers: [FormlyWrapper.FormField],
  },
  {
    name: FormlyFieldType.AsyncSelect,
    component: FormlyFieldAsyncSelectComponent,
    wrappers: [FormlyWrapper.FormField],
  },
  {
    name: FormlyFieldType.Button,
    component: FormlyFieldButtonComponent,
    wrappers: [FormlyWrapper.FormField],
    defaultOptions: {
      templateOptions: {
        btnType: 'default',
        type: 'button',
      },
    },
  },
  {
    name: FormlyFieldType.AssetUpload,
    component: FormlyFieldAssetUploadComponent,
    wrappers: [FormlyWrapper.FormField],
    defaultOptions: {
      templateOptions: {
        type: 'button',
        btnType: 'default',
        text: 'upload file',
      },
    },
  },
  {
    name: FormlyFieldType.Repeat,
    component: FormlyFieldRepeatSectionComponent,
    wrappers: [FormlyWrapper.FormField],
  },
  {
    name: FormlyFieldType.AutoRepeat,
    component: FormlyFieldAutoRepeatSectionComponent,
    wrappers: [FormlyWrapper.FormField],
  },
  {
    name: FormlyFieldType.Text,
    component: FormlyFieldTextComponent,
    wrappers: [FormlyWrapper.FormField],
  },
  {
    name: FormlyFieldType.Expansion,
    component: FormlyFieldExpansionComponent,
    wrappers: [FormlyWrapper.FormField],
  },
  {
    name: FormlyFieldType.Selection,
    component: FormlyFieldSelectionComponent,
    wrappers: [FormlyWrapper.FormField],
  },
  {
    name: FormlyFieldType.Date,
    component: FormlyFieldDateComponent,
    wrappers: [FormlyWrapper.FormField],
  },
];

const WRAPPERS = [
  { name: FormlyWrapper.InlineLabel, component: FormlyWrapperInlineLabelComponent },
  { name: FormlyWrapper.LabelTooltip, component: FormlyWrapperLabelTooltipComponent },
  { name: FormlyWrapper.Panel, component: FormlyWrapperPanelComponent },
  { name: FormlyWrapper.Expansion, component: FormlyWrapperExpansionComponent },
  { name: FormlyWrapper.Slider, component: FormlySliderWrapperComponent },
];

const MaterialModules = [
  MatButtonModule,
  MatFormFieldModule,
  MatIconModule,
  MatSlideToggleModule,
  MatTooltipModule,
];

const Components = [
  FormlyFieldAsyncSelectComponent,
  FormlyFieldAutoRepeatSectionComponent,
  FormlyFieldButtonComponent,
  FormlyFieldInlineScrollableRadioComponent,
  FormlyFieldInputComponent,
  FormlyFieldRepeatSectionComponent,
  FormlyFieldSelectComponent,
  FormlyFieldTextComponent,
  FormlyFormComponent,
  FormlyWrapperInlineLabelComponent,
  FormlyWrapperLabelTooltipComponent,
  FormlyWrapperPanelComponent,
];

const noSpacesAllowedValidator = (control: AbstractControl): ValidationErrors | null =>
  !control.value || /^\S*$/gm.test(control.value) ? null : { [FormlyValidator.NoSpacesAllowed]: true };

const alphaNumeric = (control: AbstractControl): ValidationErrors | null =>
  !control.value || /^[A-Za-z0-9]+$/.test(control.value) ? null : { [FormlyValidator.Alphanumeric]: true };

const alphaNumericAndSlash = (control: AbstractControl): ValidationErrors | null =>
  !control.value || /^[A-Za-z0-9/]+$/.test(control.value) ? null : { [FormlyValidator.AlphanumericAndSlash]: true };

const alphaNumericAndSlashAndWildcard = (control: AbstractControl): ValidationErrors | null =>
  !control.value || /^[A-Za-z0-9/+*]+$/.test(control.value) ? null : { [FormlyValidator.AlphanumericAndSlashAndWildcard]: true };

const alphaNumericAndDash = (control: AbstractControl): ValidationErrors | null =>
  !control.value || /^[A-Za-z0-9-]+$/.test(control.value) ? null : { [FormlyValidator.AlphanumericAndDash]: true };

const alphaNumericAndDashWithSpaces = (control: AbstractControl): ValidationErrors | null =>
  !control.value || /^[A-Za-z0-9-\s]+$/.test(control.value) ? null : { [FormlyValidator.AlphanumericAndDashWithSpaces]: true };

const minLengthValidationMessage = (error: any, field: FormlyFieldConfig) => {
  return `Must be at least ${field.props?.minLength} characters long`;
};

const maxLengthValidationMessage = (error: any, field: FormlyFieldConfig) => {
  return `Must not exceed ${field.props?.maxLength} characters`;
};

const minValidationMessage = (error: any, field: FormlyFieldConfig) => {
  return `The minimum allowed value is ${field.props?.min}`;
};

const maxValidationMessage = (error: any, field: FormlyFieldConfig) => {
  return `The maximum allowed value is ${field.props?.max}`;
};

@NgModule({
  declarations: [...Components],
  imports: [
    GenericButtonComponent,
    CommonModule,
    FormsModule,
    ReactiveFormsModule,
    NgSelectModule,
    FormlySelectModule,
    FormlyMatToggleModule,
    NgxAngularQueryBuilderModule,
    FormlyMatFormFieldModule,
    FormlyModule.forRoot({
      extensions: [
        // { name: 'defaultOptions', extension: { prePopulate: applyModelChangeDebounce } },
      ],
      types: FORMLY_CUSTOM_TEMPLATES,
      wrappers: WRAPPERS,
      validators: [
        { name: FormlyValidator.NoSpacesAllowed, validation: noSpacesAllowedValidator },
        { name: FormlyValidator.Alphanumeric, validation: alphaNumeric },
        { name: FormlyValidator.AlphanumericAndSlash, validation: alphaNumericAndSlash },
        { name: FormlyValidator.AlphanumericAndSlashAndWildcard, validation: alphaNumericAndSlashAndWildcard },
        { name: FormlyValidator.AlphanumericAndDash, validation: alphaNumericAndDash },
        { name: FormlyValidator.AlphanumericAndDashWithSpaces, validation: alphaNumericAndDashWithSpaces },
      ],
      validationMessages: [
        { name: 'required', message: 'This field is required' },
        { name: 'minLength', message: minLengthValidationMessage },
        { name: 'maxLength', message: maxLengthValidationMessage },
        { name: 'min', message: minValidationMessage },
        { name: 'max', message: maxValidationMessage },
        { name: FormlyValidator.NoSpacesAllowed, message: 'Spaces are not allowed for this field' },
        { name: FormlyValidator.Alphanumeric, message: 'Only letters and digits are allowed for this field' },
        {
          name: FormlyValidator.AlphanumericAndSlash,
          message: 'Only letters, digits and slashes are allowed for this field',
        },
        {
          name: FormlyValidator.AlphanumericAndSlashAndWildcard,
          message: 'Only letters, digits, slashes and wildcards are allowed for this field',
        },
        {
          name: FormlyValidator.AlphanumericAndDash,
          message: 'Only letters, digits and dashes are allowed for this field',
        },
        {
          name: FormlyValidator.AlphanumericAndDashWithSpaces,
          message: 'Only letters, digits, dashes and spaces are allowed for this field',
        },
      ],
    }),
    FormlyBootstrapModule,
    FormControlsModule,
    DragDropModule,
    ...MaterialModules,
    NgxDaterangepickerMd,
    DragDropDirective,
    SimplePreloaderComponent,
    SafePipe,
    KeyValuePipe,
    NoScrollDirective,
    InputTrimDirective,
    ContentContainerComponent,
    FormlyFieldAssetUploadComponent,
    FormlyFieldExpansionComponent,
    FormlyFieldSelectionComponent,
    FormlyFieldDateComponent,
  ],
  exports: [ReactiveFormsModule, FormlyModule, ...Components],
})
export class FormlyformModule { }
